import {
  Box,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { IoSend } from "react-icons/io5";
import {
  fetchChatHistory,
  sendUserQueryToAiModel,
} from "../../api/chatService.js";
import { BACKGROUND_COLOR_DARK } from "../../constants";
import { getFormattedDate } from "../../utils/UTILITY_METHODS";
import ChatBubble from "./ChatBubble";
import TimeStampDivider from "./TimeStampDivider";

const ChatBox = ({ isAtlasTextNeeded = false }) => {
  const [chatHistory, setChatHistory] = useState([]);
  const [typingAnimation, setTypingAnimation] = useState(false);
  const [isAiWritingText, setIsAiWritingText] = useState(false);
  const [isChatInputDisabled, setIsChatInputDisabled] = useState(false);

  // Internal ref for chat container
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const toast = useToast();

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const getChatHistory = async () => {
      setIsAiWritingText(true);
      try {
        let data = await fetchChatHistory();

        // Check if messages list is empty and add default message if necessary
        if (data && data.data.messages.length === 0) {
          data = {
            ...data,
            data: {
              ...data.data,
              messages: [
                {
                  created_at: new Date().toISOString(),
                  intent: null,
                  message_content:
                    "Hi, I'm Atlas - your loan assistant, what kind of loan are you looking for?",
                  message_type: "text",
                  meta: {},
                  role: "model",
                  sub_intent: null,
                },
              ],
              page: 1,
              total: 1,
            },
          };
        }

        const messages = data.data.messages.reverse();
        setChatHistory(messages);
        // setChatHistory([
        //   {
        //     created_at: "2024-10-28T13:14:31",
        //     intent: "general_query",
        //     message_content:
        //       "Credit team has asked for GJEPC RCMC registration for Danby Jewels last month. Based on my understanding these companies have a similar profile. Do you want me to collect this document?",
        //     message_type: "text",
        //     meta: {},
        //     role: "model",
        //     sub_intent: "info",
        //   },
        //   {
        //     created_at: "2024-10-28T13:14:28",
        //     intent: "general_query",
        //     message_content: "Yes",
        //     message_type: "text",
        //     meta: {},
        //     role: "user",
        //     sub_intent: "info",
        //   },
        //   {
        //     created_at: "2024-10-28T13:14:31",
        //     intent: "general_query",
        //     message_content:
        //       "I have reached out to the borrower on Whatsapp. Awaiting a response.",
        //     message_type: "text",
        //     meta: {},
        //     role: "model",
        //     sub_intent: "info",
        //   },
        //   {
        //     created_at: "2024-10-28T13:14:31",
        //     intent: "general_query",
        //     message_content:
        //       "According to me, the PAN card image is blurred. Let me know if I should reach out to the borrower for a replacement.",
        //     message_type: "text",
        //     meta: {},
        //     id: 3,
        //     role: "model",
        //     sub_intent: "info",
        //   },
        // ]);
        setIsAiWritingText(false);
      } catch (error) {
        toast({
          title: "Something went wrong",
          description: error.message,
          position: "top",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setIsAiWritingText(false);
      }
    };

    getChatHistory();
  }, [toast]);

  useEffect(() => {
    if (!isChatInputDisabled && inputRef.current) {
      inputRef.current.focus(); // Focus the chat input when it is enabled
    }
  }, [isChatInputDisabled]);

  const handleSendUserQueryToAiModel = async (
    event,
    sendButtonClick = null
  ) => {
    if (event.key === "Enter" || sendButtonClick === "sendIconClicked") {
      const inputText = inputRef.current.value;
      if (!inputText.trim()) return;
      inputRef.current.value = "";
      setTypingAnimation(true);
      scrollToBottom(); // scroll down when user asks a query

      setChatHistory((prev) => {
        const updatedChat = [
          ...prev,
          {
            created_at: getFormattedDate(),
            message_content: inputText,
            role: "user",
          },
        ];
        return updatedChat;
      });
      setIsAiWritingText(true);
      scrollToBottom(); // scroll down when user asks a query

      const applicationId = "application123",
        role = "user",
        messageType = "text";

      try {
        const data = await sendUserQueryToAiModel(
          applicationId,
          role,
          inputText,
          messageType
        );
        const responseAiMessageStructure = {
          created_at: getFormattedDate(),
          message_content: data.data.answer || "Something went Wrong",
          role: "model",
        };
        setChatHistory((prev) => {
          const updatedChat = [...prev, responseAiMessageStructure];
          return updatedChat;
        });

        // setChatHistory((prev) => {
        //   const updatedChat = [
        //     ...prev,
        //     {
        //       created_at: getFormattedDate(),
        //       message_content:
        //         "I have reached out to the borrower on whatsapp. Awaiting a response.",
        //       role: "model",
        //     },
        //   ];
        //   return updatedChat;
        // });

        setTypingAnimation(false);
        scrollToBottom(); // Scroll after receiving AI response
      } catch (error) {
        setTypingAnimation(false);
        setIsAiWritingText(false);
        setChatHistory((prev) => {
          const updatedChat = [
            ...prev,
            {
              created_at: getFormattedDate(),
              message_content:
                "I am unable to get the query, please give more details.",
              role: "model",
            },
          ];
          return updatedChat;
        });
        scrollToBottom(); // Scroll in case of error
      }
    }
  };

  useEffect(() => {
    let intervalId;
    if (isChatInputDisabled) {
      intervalId = setInterval(scrollToBottom, 500);
    }
    return () => clearInterval(intervalId);
  }, [chatHistory, typingAnimation, isAiWritingText, isChatInputDisabled]);

  useEffect(() => {
    setIsChatInputDisabled(typingAnimation || isAiWritingText);
    scrollToBottom();
  }, [typingAnimation, isAiWritingText, chatHistory]);

  return (
    <Box
      boxShadow="rgba(17, 17, 26, 0.05) 0px 1px 0px, rgba(17, 17, 26, 0.1) 0px 0px 8px;"
      borderRadius="2xl"
      padding={4} // Reduced padding to lessen space
      marginRight={3}
      style={{
        height: "85vh",
        display: "flex",
        flexDirection: "column",
      }}
      backgroundColor={"white"}
      width={"95%"}
    >
      <Heading as="h2" size="md">
        Chat with your {isAtlasTextNeeded ? "Atlas" : "loan assistant"}
      </Heading>
      {
        <Text
          color={isAtlasTextNeeded ? "white" : "gray.500"}
          fontSize={isAtlasTextNeeded ? "sm" : "md"}
          mt={isAtlasTextNeeded ? 0 : 2}
        >
          Connect with your loan assistant for personalized support and expert
          guidance
        </Text>
      }
      <Box
        className={isAtlasTextNeeded ? "-mt-4" : "mt-4"}
        flex="1"
        overflowY="auto"
        maxHeight="calc(100vh - 200px)"
      >
        <TimeStampDivider value={"Today"} />
        <Box>
          {chatHistory.map((message, index) => (
            <ChatBubble
              key={message.created_at + " " + message.role}
              message={message.message_content}
              isCustomer={message.role === "user"}
              isLastMessage={chatHistory?.length - 1 === index}
              setIsAiWritingText={setIsAiWritingText}
              isNewMessage={true}
              isRedAlertRequired={message.id === 3}
            />
          ))}
          {typingAnimation && (
            <ChatBubble isCustomer={false} beatLoader={true} />
          )}
          {/* Empty div for automatically scrolling to bottom */}
          <div ref={chatContainerRef} />
        </Box>
      </Box>

      {/* Input Box */}
      <Box mt={2}>
        <InputGroup size="md">
          <Input
            pr="3.5rem"
            placeholder="Type your query here..."
            focusBorderColor={BACKGROUND_COLOR_DARK}
            py={4}
            ref={inputRef}
            onKeyDown={handleSendUserQueryToAiModel}
            disabled={isChatInputDisabled}
          />
          <InputRightElement width="4.5rem" height="100%">
            <IoSend
              className={`h-5 w-5 ${
                isChatInputDisabled ? "cursor-not-allowed" : "cursor-pointer"
              }`}
              style={{
                color: isChatInputDisabled ? "gray" : BACKGROUND_COLOR_DARK,
                opacity: isChatInputDisabled ? 0.5 : 1,
              }}
              onClick={(e) => {
                if (!isChatInputDisabled) {
                  handleSendUserQueryToAiModel(e, "sendIconClicked");
                }
              }}
            />
          </InputRightElement>
        </InputGroup>
      </Box>
    </Box>
  );
};

export default ChatBox;
